import { Injectable } from '@angular/core';
import { EqualizerBand, EqualizerPreset } from '../../objects/media-player/equalizer-preset';

@Injectable({
  providedIn: 'root'
})
export class EqualizerService {
  private audioContext: AudioContext | null = null;
  private filters: BiquadFilterNode[] = [];
  private gainNode: GainNode | null = null;
  private isEnabled = true;
  private hasBeenInitialized = false;
  private source: MediaElementAudioSourceNode | null = null;

  constructor() {}

  /**
   * Initialize the Equalizer with an audio element
   */
  initializeEqualizer(audioElement: HTMLAudioElement): boolean {
    try {
      // Prevent re-initialization
      if (this.hasBeenInitialized) {
        console.log('Equalizer already initialized');
        return true;
      }

      if (!audioElement) {
        console.error('Audio element is null or undefined');
        return false;
      }

      if (!this.audioContext) {
        const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          console.error('Web Audio API not supported');
          return false;
        }
        this.audioContext = new AudioContextClass();
        console.log('AudioContext created, state:', this.audioContext!.state);
      }

      // Resume audio context if suspended (critical for some browsers)
      if (this.audioContext!.state === 'suspended') {
        console.log('Resuming suspended audio context');
        this.audioContext!.resume().then(() => {
          console.log('Audio context resumed successfully');
        }).catch(err => console.error('Failed to resume audio context:', err));
      }

      // Check if audio element already has a media element source
      if ((audioElement as any).mediaElementSource !== undefined) {
        console.log('MediaElementSource already exists, using it');
        this.source = (audioElement as any).mediaElementSource;
      } else {
        // Critical: createMediaElementSource must be called at the right time
        // It should work even if audio is playing, but we try to ensure clean state
        console.log('Audio element paused state:', audioElement.paused, 'duration:', audioElement.duration);

        try {
          this.source = this.audioContext!.createMediaElementSource(audioElement);
          (audioElement as any).mediaElementSource = this.source; // Cache it
          console.log('MediaElementSource created successfully');
        } catch (e) {
          console.error('Failed to create MediaElementSource:', e);
          // The error is likely because createMediaElementSource was already called on this element
          // Try to recover by looking for existing source
          return false;
        }
      }

      // Create gain node for master volume control
      this.gainNode = this.audioContext!.createGain();
      this.gainNode.gain.value = 1.0;
      console.log('GainNode created with value:', this.gainNode.gain.value);

      // Create filters for each frequency band
      this.filters = [];
      const frequencies = [60, 250, 1000, 4000, 16000];

      frequencies.forEach((freq, index) => {
        const filter = this.audioContext!.createBiquadFilter();
        filter.type = 'peaking';
        filter.frequency.value = freq;
        filter.Q.value = 1.2;
        filter.gain.value = 0;
        this.filters.push(filter);
        console.log(`Filter ${index} created for ${freq}Hz`);
      });

      // Build the audio graph: source -> gainNode -> filters -> destination
      console.log('Connecting audio nodes...');
      try {
        this.source!.connect(this.gainNode!);
        console.log('✓ Source → GainNode connected');

        let prevNode: AudioNode = this.gainNode!;
        this.filters.forEach((filter, i) => {
          prevNode.connect(filter);
          console.log(`✓ Filter ${i} connected (${[60, 250, 1000, 4000, 16000][i]}Hz)`);
          prevNode = filter;
        });

        prevNode.connect(this.audioContext!.destination);
        console.log('✓ Filters → AudioContext.destination connected');

        console.log('Audio graph connection complete');
      } catch (connectionError) {
        console.error('Error connecting audio nodes:', connectionError);
        return false;
      }

      this.hasBeenInitialized = true;
      console.log('✓ Equalizer initialization complete - audio should now flow through filters');
      return true;
    } catch (error) {
      console.error('Error initializing equalizer:', error);
      this.hasBeenInitialized = false;
      return false;
    }
  }

  /**
   * Ensure the audio graph is built around `audioElement` and running.
   *
   * Safe to call on every play: once the graph exists this only resumes a
   * context the browser suspended (autoplay policy suspends any context
   * created before the first user gesture, and routing an element into a
   * suspended context silences it).
   */
  attach(audioElement: HTMLAudioElement): boolean {
    if (!this.isSupported() || !audioElement) {
      return false;
    }

    if (!this.hasBeenInitialized) {
      this.initializeEqualizer(audioElement);
    }

    this.resumeContext();
    return this.isInitialized();
  }

  /**
   * Push the store's equalizer state onto the filters. Flattens the bands
   * when the equalizer is switched off, and does nothing until the graph
   * exists, so it is safe to call before anything has played.
   */
  applyState(preset: EqualizerPreset, isEnabled: boolean): void {
    this.isEnabled = isEnabled;

    if (!this.isInitialized()) {
      return;
    }

    if (isEnabled) {
      this.applyPreset(preset);
    } else {
      this.resetFilters();
    }
  }

  private resumeContext(): void {
    if (this.audioContext?.state === 'suspended') {
      this.audioContext
        .resume()
        .catch(err => console.warn('Could not resume audio context:', err));
    }
  }

  /**
   * Apply an equalizer preset
   */
  applyPreset(preset: EqualizerPreset): void {
    if (!this.audioContext || this.filters.length === 0) {
      console.warn('Equalizer not initialized');
      return;
    }

    preset.bands.forEach((band, index) => {
      if (this.filters[index]) {
        this.filters[index].gain.value = band.gain;
      }
    });
  }

  /**
   * Update a specific frequency band
   */
  updateBand(bandIndex: number, gain: number): void {
    if (this.filters[bandIndex]) {
      this.filters[bandIndex].gain.value = gain;
    }
  }

  /**
   * Reset all filters to 0 gain
   */
  resetFilters(): void {
    this.filters.forEach(filter => {
      filter.gain.value = 0;
    });
  }

  /**
   * Enable/Disable the equalizer
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.resetFilters();
    }
  }

  /**
   * Check if equalizer is enabled
   */
  getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Check if equalizer is initialized
   */
  isInitialized(): boolean {
    return this.hasBeenInitialized && this.filters.length > 0 && this.source !== null;
  }

  /**
   * Get the current audio context (for advanced usage)
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Reset the equalizer initialization so it can be recreated cleanly.
   * Disconnects the old audio graph and resets the internal state.
   */
  reset(audioElement?: HTMLAudioElement): void {
    console.log('Resetting equalizer...');

    if (this.source) {
      try {
        this.source.disconnect();
      } catch (err) {
        console.warn('Error disconnecting source during reset:', err);
      }
    }

    if (this.gainNode) {
      try {
        this.gainNode.disconnect();
      } catch (err) {
        console.warn('Error disconnecting gain node during reset:', err);
      }
    }

    this.filters.forEach(filter => {
      try {
        filter.disconnect();
      } catch (err) {
        console.warn('Error disconnecting filter during reset:', err);
      }
    });

    this.filters = [];
    this.gainNode = null;
    this.source = null;
    this.hasBeenInitialized = false;

    if (audioElement && (audioElement as any).mediaElementSource !== undefined) {
      try {
        delete (audioElement as any).mediaElementSource;
      } catch {
        (audioElement as any).mediaElementSource = undefined;
      }
    }

    if (this.audioContext) {
      try {
        this.audioContext.close().catch(err => console.warn('Error closing audio context during reset:', err));
      } catch (err) {
        console.warn('Error closing audio context during reset:', err);
      }
      this.audioContext = null;
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
    this.audioContext = null;
    this.filters = [];
    this.gainNode = null;
    this.source = null;
    this.hasBeenInitialized = false;
  }

  /**
   * Check if Web Audio API is supported
   */
  isSupported(): boolean {
    // Called from the equalizer template, so it also runs during prerendering
    // where there is no `window`.
    if (typeof window === 'undefined') {
      return false;
    }
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    return !!AudioContextClass;
  }
}
